/**
 * Payment history fixture data.
 * Per-persona payment records for R4.11 Payment History.
 * Dates are re-anchored to "now" at access via a Proxy (see demo-clock.ts) so a
 * parked demo never shows stale absolute payment dates. The literal dates in the
 * ledger below are the authoring-era reference; consumers read the re-anchored view.
 */

import { reanchorDemoDate } from "./demo-clock";

export type PaymentStatus = "confirmed" | "pending" | "failed";

export interface PaymentRecord {
  id: string;
  amount: number;
  method: string;
  date: string;
  status: PaymentStatus;
}

// `label` is the English SSoT (kept for mobile + any not-yet-migrated surface);
// `labelKey` resolves via t() for the trilingual web history table (D3 labelKey
// precedent — see SR_STATUS_TOKENS). Additive: SSoT stays here.
export const PAYMENT_STATUS_TOKENS: Record<
  PaymentStatus,
  { bg: string; text: string; label: string; labelKey: string }
> = {
  confirmed: {
    bg: "bg-semantic-success/10",
    // AA green for text on the success tint (W7/D4 — base #1A8754 is ~4.0:1 as
    // a chip label; the -text step is ~5.9:1). status-* token exists in both
    // web + mobile configs.
    text: "text-status-success-text",
    label: "Confirmed",
    labelKey: "payStatus.confirmed",
  },
  pending: {
    bg: "bg-brand-accent/10",
    // AA amber for text — brand-accent-dark (#E6A118 gold) is only ~2.2:1; the
    // warning-text step (#92400E) reads AA on the accent tint (W7/D4).
    text: "text-status-warning-text",
    label: "Pending",
    labelKey: "payStatus.pending",
  },
  failed: {
    bg: "bg-semantic-error/10",
    text: "text-semantic-error",
    label: "Failed",
    labelKey: "payStatus.failed",
  },
};

const PAYMENTS_LEDGER: Record<string, PaymentRecord[]> = {
  P1: [
    { id: "PAY-20260501", amount: 298.40, method: "Visa •••• 6411", date: "2026-05-01", status: "confirmed" },
    { id: "PAY-20260401", amount: 287.30, method: "Visa •••• 6411", date: "2026-04-01", status: "confirmed" },
    { id: "PAY-20260301", amount: 252.19, method: "Mastercard •••• 8823", date: "2026-03-03", status: "confirmed" },
    { id: "PAY-20260201", amount: 226.64, method: "Visa •••• 6411", date: "2026-02-01", status: "confirmed" },
    { id: "PAY-20260101", amount: 237.12, method: "Visa •••• 6411", date: "2026-01-02", status: "confirmed" },
    { id: "PAY-20251201", amount: 269.80, method: "Visa •••• 6411", date: "2025-12-01", status: "confirmed" },
    { id: "PAY-20251101", amount: 318.45, method: "Mastercard •••• 8823", date: "2025-11-03", status: "confirmed" },
    { id: "PAY-20251001", amount: 342.17, method: "Visa •••• 6411", date: "2025-10-01", status: "confirmed" },
    { id: "PAY-20250901", amount: 356.90, method: "Visa •••• 6411", date: "2025-09-02", status: "confirmed" },
    { id: "PAY-20250801", amount: 371.28, method: "Visa •••• 6411", date: "2025-08-01", status: "confirmed" },
    { id: "PAY-20250701", amount: 389.40, method: "Visa •••• 6411", date: "2025-07-01", status: "confirmed" },
    { id: "PAY-20250601", amount: 345.62, method: "Mastercard •••• 8823", date: "2025-06-02", status: "confirmed" },
  ],
  P2: [
    { id: "PAY-20260501", amount: 640.00, method: "ACH •••• 4201", date: "2026-05-01", status: "confirmed" },
    { id: "PAY-20260401", amount: 612.50, method: "ACH •••• 4201", date: "2026-04-01", status: "confirmed" },
    { id: "PAY-20260301", amount: 588.75, method: "ACH •••• 4201", date: "2026-03-01", status: "confirmed" },
    { id: "PAY-20260201", amount: 545.30, method: "ACH •••• 4201", date: "2026-02-01", status: "confirmed" },
    { id: "PAY-20260101", amount: 510.00, method: "ACH •••• 4201", date: "2026-01-02", status: "confirmed" },
    { id: "PAY-20251201", amount: 578.20, method: "ACH •••• 4201", date: "2025-12-01", status: "confirmed" },
  ],
  P3: [
    { id: "PAY-20260501", amount: 71100.00, method: "Wire Transfer", date: "2026-05-01", status: "confirmed" },
    { id: "PAY-20260401", amount: 68450.00, method: "Wire Transfer", date: "2026-04-01", status: "confirmed" },
    { id: "PAY-20260301", amount: 65200.00, method: "Wire Transfer", date: "2026-03-01", status: "confirmed" },
    { id: "PAY-20260201", amount: 52800.00, method: "Wire Transfer", date: "2026-02-01", status: "confirmed" },
    { id: "PAY-20260101", amount: 48900.00, method: "Wire Transfer", date: "2026-01-02", status: "confirmed" },
    { id: "PAY-20251201", amount: 58700.00, method: "Wire Transfer", date: "2025-12-01", status: "confirmed" },
  ],
  P4: [
    { id: "PAY-20260501", amount: 2488.74, method: "Batch — Visa •••• 3319", date: "2026-05-01", status: "confirmed" },
    { id: "PAY-20260401", amount: 2312.50, method: "Batch — Visa •••• 3319", date: "2026-04-01", status: "confirmed" },
    { id: "PAY-20260301", amount: 2198.00, method: "Batch — Visa •••• 3319", date: "2026-03-01", status: "confirmed" },
    { id: "PAY-20260201", amount: 2045.60, method: "Batch — Visa •••• 3319", date: "2026-02-01", status: "confirmed" },
    { id: "PAY-20260101", amount: 1987.30, method: "Batch — Visa •••• 3319", date: "2026-01-02", status: "confirmed" },
    { id: "PAY-20251201", amount: 2156.80, method: "Batch — Visa •••• 3319", date: "2025-12-01", status: "confirmed" },
  ],
  P5: [],
};

/**
 * Payment history with dates re-anchored to "now" at access. Reads like the
 * ledger but every `date` (and the date-derived `id`) is shifted to keep the
 * demo current. Consumed unchanged by web + mobile via `FIXTURE_PAYMENTS[id]`.
 */
export const FIXTURE_PAYMENTS: Record<string, PaymentRecord[]> = new Proxy(PAYMENTS_LEDGER, {
  get(target, key) {
    const rows = target[key as string];
    if (!Array.isArray(rows)) return rows;
    return rows.map((p) => {
      const date = reanchorDemoDate(p.date);
      return { ...p, date, id: `PAY-${date.replaceAll("-", "")}` };
    });
  },
});
